import styled, { keyframes } from "styled-components";
import BannerBG from "common/assets/image/saasModern/banner-texture.png";
import { ORBIT_MS } from "./seeInAction.style";

// The shine on the gold figure: a brighter band sweeps across it in 1.2 s,
// then the figure rests. One sweep per two laps of the light of See in
// action, starting between its laps, so the two never pull at the eye at once
// and nothing blinks all the time.
const SHINE_MS = 2 * ORBIT_MS;
const SHINE_DELAY_MS = 2500;
const heroShine = keyframes`
  0% { background-position: 100% 0, 0 0; }
  15% { background-position: 0% 0, 0 0; }
  100% { background-position: 0% 0, 0 0; }
`;

export const BannerWrapper = styled.section`
  /* The band of audit figures (ProofStrip) is the last thing in the hero. The
     wedge that cuts the hero from the next section is drawn under it
     (::after, in the background of that section), and the bottom padding is its height plus a little air, so no
     figure ever touches the cut. */
  --wedge-h: max(5vw, 24px);
  position: relative;
  padding: 0 0 calc(var(--wedge-h) + 12px) 0;
  /* The top layer deepens the lower part of the hero a little, so the small
     text of the band of audit figures keeps 4.5:1 where it sits on the light
     end of the gradient. */
  background-image: linear-gradient(
      180deg,
      rgba(15, 33, 55, 0) 40%,
      rgba(15, 33, 55, 0.32) 62%,
      rgba(15, 33, 55, 0.32) 100%
    ),
    url(${BannerBG}),
    linear-gradient(35deg, rgb(51, 70, 102) 0%, rgb(113, 151, 214) 100%);
  background-size: cover;
  @media (max-width: 767px) {
    background-image: linear-gradient(
        180deg,
        rgba(15, 33, 55, 0) 30%,
        rgba(15, 33, 55, 0.36) 50%,
        rgba(15, 33, 55, 0.36) 100%
      ),
      url(${BannerBG}),
      linear-gradient(35deg, rgb(51, 70, 102) 0%, rgb(113, 151, 214) 100%);
  }
  background-position: top center;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: var(--wedge-h);
    /* the background of the section under the hero (CaseStudiesWrapper);
       change both together, or a lighter or darker wedge shows at the cut */
    background: #f9fbfd;
    clip-path: polygon(0 70%, 100% 0, 100% 100%, 0 100%);
    pointer-events: none;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

/* The first screen. On desktop the space under the navigation (--top) and
   between the hero and the band of audit figures (--band-gap, always clearly
   larger than the gaps inside the text column) follows the window height. The
   top of that band therefore shows at the bottom of the first screen: on tall
   windows its heading and figures, on short laptops its top rule and heading,
   so the page never looks finished. */
export const HeroStage = styled.div`
  --nav-h: 112px;
  --gutter: 5vw;
  /* A soft shade behind the text keeps white text readable (4.5:1) on the
     lighter parts of the hero gradient; it fades out before any edge. */
  background: radial-gradient(
    ellipse 62% 72% at 22% 52%,
    rgba(15, 33, 55, 0.34),
    rgba(15, 33, 55, 0) 72%
  );
  display: flex;
  align-items: center;
  padding: var(--nav-h) var(--gutter) 40px;
  @media (min-width: 992px) {
    --top: clamp(40px, 6vh, 110px);
    --band-gap: clamp(72px, 9vh, 140px);
    padding-top: calc(var(--nav-h) + var(--top));
    padding-bottom: var(--band-gap);
  }
  /* Short laptop windows (1280x720, 1366x768): the hero is never shorter
     than the window minus the top rule and heading of the band of audit
     figures (--band-peek, see ProofStrip), so it does not look squeezed and
     the band still peeks. */
  @media (min-width: 992px) and (max-height: 849px) {
    --band-peek: 64px;
    min-height: calc(100vh - var(--band-peek));
    min-height: calc(100svh - var(--band-peek));
    padding-top: var(--nav-h);
    padding-bottom: 40px;
  }
  @media (max-width: 991px) {
    --nav-h: 104px;
    --gutter: 24px;
    padding-bottom: 48px;
    background: linear-gradient(
      180deg,
      rgba(15, 33, 55, 0.42) 0%,
      rgba(15, 33, 55, 0.3) 55%,
      rgba(15, 33, 55, 0) 100%
    );
  }
  @media (max-width: 767px) {
    --nav-h: 96px;
    --gutter: 30px;
  }
`;

export const HeroGrid = styled.div`
  --copy-w: clamp(450px, 37vw, 820px);
  --gap: clamp(40px, 4vw, 96px);
  display: grid;
  grid-template-columns: var(--copy-w) minmax(0, 1fr);
  column-gap: var(--gap);
  align-items: center;
  width: 100%;
  max-width: 2200px;
  margin: 0 auto;
  color: #fff;

  h1 {
    margin: 0 0 20px;
    color: #fff;
    /* sized so the longer line, "Typical savings: 13-44%" (about 10.6 em),
       fits the text column in one line: the heading keeps two lines */
    font-size: clamp(40px, 3.4vw, 76px);
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: -0.03em;
    text-wrap: balance;

    /* two lines, both white: the promise, then the result. Only the figure
       is gold (the yellow stays the download button's own colour). */
    > span {
      display: block;
    }
    .hero_gold {
      display: inline-block;
      white-space: nowrap;
      /* without background-clip: text the figure is plain gold */
      color: #ffd27a;
      /* a soft warm light around it, as around See in action */
      filter: drop-shadow(0 0 0.3em rgba(253, 160, 50, 0.35));
    }
    @supports ((-webkit-background-clip: text) or (background-clip: text)) {
      .hero_gold {
        /* the gold of See in action (seeInAction.style.js): its core
           #ffe2b0 at the top, its light rgb(255, 206, 120), and at the foot
           its deeper stop rgba(253, 160, 50, 0.6) as it shows over that
           light, rgb(254, 178, 78); the deepest pixel keeps 3:1 on the
           lightest blue behind the figure. Over it lies a brighter band (the
           shine), parked outside the figure until it sweeps across. */
        background-image: linear-gradient(
            100deg,
            rgba(255, 248, 232, 0) 40%,
            rgba(255, 248, 232, 0.95) 50%,
            rgba(255, 248, 232, 0) 60%
          ),
          linear-gradient(
            180deg,
            #ffe2b0 8%,
            rgb(255, 206, 120) 52%,
            rgb(254, 178, 78) 100%
          );
        background-size: 250% 100%, 100% 100%;
        background-repeat: no-repeat;
        background-position: 0% 0, 0 0;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        animation: ${heroShine} ${SHINE_MS}ms ease-in-out ${SHINE_DELAY_MS}ms
          infinite;
      }
    }
    /* reduced motion: the gold stands still, no shine */
    @media (prefers-reduced-motion: reduce) {
      .hero_gold {
        animation: none;
      }
    }
  }

  /* The brand line over the heading: small, white, about a third of the
     heading's size; never smaller than body text. */
  .hero_kicker {
    margin: 0 0 0.45em;
    color: #fff;
    font-size: clamp(16px, 1.15vw, 26px);
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: 0.01em;
  }

  /* The quiet link to the audit cases: plain link text the size of the
     sentence, underlined on hover, no outline or fill. It keeps its words
     together, so on a narrow column it moves to a line of its own. */
  .hero_cases {
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    white-space: nowrap;
    color: #fff;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.5);
    text-underline-offset: 0.2em;
    transition: text-decoration-color 0.15s ease;

    svg {
      width: 0.62em;
      height: 0.75em;
      flex: none;
    }

    &:hover,
    &:focus-visible {
      text-decoration-color: #fff;
    }
    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 3px;
      border-radius: 2px;
    }
  }

  .hero_lead {
    max-width: 34em;
    margin: 0 0 36px;
    color: #fff;
    font-size: clamp(18px, 1.35vw, 28px);
    line-height: 1.5;
    text-wrap: balance;
  }

  .hero_reassure {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.5em;
    margin: 0;
    font-size: clamp(16px, 0.84vw, 21px);
    line-height: 1.5;

    span {
      white-space: nowrap;
    }

    i {
      font-style: normal;
      opacity: 0.6;
    }
  }

  .hero_reassure {
    margin-top: 14px;
    color: #fff;
  }

  /* The download button keeps its line of facts right under it; See in
     action comes after them. Where the text column is wide enough it stands
     next to the download button instead (if a longer system name still does
     not fit, it wraps under the button and never runs into the film). */
  .hero_action {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 14px;

    /* the download button stays on top of the glow of See in action */
    > :first-child {
      order: 1;
      position: relative;
      z-index: 2;
    }
    .hero_reassure {
      order: 2;
      flex-basis: 100%;
    }
    .see_in_action {
      order: 3;
      margin-top: 18px;
    }
  }

  /* Large screens: the button grows with the heading and the film. */
  @media (min-width: 992px) {
    .hero_action button {
      height: clamp(56px, 2.9vw, 74px);
      padding: 0 clamp(28px, 1.5vw, 40px);
      font-size: clamp(17px, 0.9vw, 22px);
    }
    .see_in_action {
      --sia-h: clamp(44px, 2.3vw, 58px);
      --sia-px: clamp(16px, 0.95vw, 28px);
      --sia-fs: clamp(14px, 0.72vw, 18px);
    }
  }
  @media (min-width: 1440px) {
    .hero_action .see_in_action {
      order: 2;
      margin-top: 0;
    }
    .hero_action .hero_reassure {
      order: 3;
    }
  }

  /* Phones and tablets: one column, the film under the button. */
  @media (max-width: 991px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: 640px;

    h1 {
      font-size: clamp(26px, calc((100vw - 64px) / 10.9), 48px);
      margin-bottom: 16px;
    }

    .hero_lead {
      margin-bottom: 28px;
      font-size: 17px;
    }

    .hero_action button {
      width: 100%;
    }

    .hero_action > a {
      display: block;
      width: 100%;
    }

    @media (max-width: 479px) {
      .hero_action button {
        padding: 0 16px;
        font-size: 16px;
      }
    }

    /* See in action takes a row under the facts, so the film comes a
       little closer: on a 360x800 phone it still ends above the cookie
       banner. */
    .hero_action .see_in_action {
      --sia-h: 44px;
      margin-top: 14px;
    }

    .hero_media {
      margin-top: 24px;
    }
  }
`;

export const DiscountWrapper = styled.div`
  text-align: center;
`;

export const DiscountLabel = styled.div`
  font-family: "Open Sans", sans-serif;
  display: inline-block;
  border-radius: 4em;
  padding: 9px 22px;
  margin-bottom: 22px;
  background-color: rgba(255, 255, 255, 0.15);
  @media (max-width: 575px) {
    padding: 7px 10px;
  }
`;

export const VideoWrapper = styled.div`
  max-width: 100%;
  position: relative;
  width: 900px;
  &:before {
    content: "";
    display: block;
    padding-top: 56.25%;
  }
  iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

export const Skeleton = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 0;
  background: linear-gradient(to right, #e5e5e5 4%, #f5f5f5 25%, #e5e5e5 36%);
  background-size: 800px 104px;
  animation: pulse 1.2s ease-in-out infinite;
  @keyframes pulse {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: 800px 0;
    }
  }
`;

export const ErrorWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ErrorMessage = styled.p`
  font-size: 1.25rem;
  font-weight: bold;
`;

export const HeroMedia = styled.figure`
  margin: 0;
  width: 100%;

  video {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    background: #0f2137;
    box-shadow: 0 12px 24px rgba(15, 33, 55, 0.3);
  }
`;
